import { motion, useScroll, useTransform } from "framer-motion"
import {Plane, Syringe, Globe2, ShieldCheck, MapPin, ArrowRight, Stethoscope, Pill, ChevronDown} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useRef, useState } from 'react'
import HavenNav from '@/components/HavenNav'

const JANE_APP_URL = "https://havenintegrativehealth.janeapp.com/login"

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
}

const services = [
  {
    icon: Syringe,
    title: "Travel Vaccinations",
    description: "Up-to-date immunizations for international travel, including routine boosters and destination-specific vaccines.",
  },
  {
    icon: Stethoscope,
    title: "Pre-Travel Consultations",
    description: "Personalized risk assessments based on your itinerary, health history, and planned activities abroad.",
  },
  {
    icon: Pill,
    title: "Preventative Medications",
    description: "Prescriptions for malaria prophylaxis, travellers' diarrhea, altitude sickness, and other travel essentials.",
  },
  {
    icon: ShieldCheck,
    title: "Health & Safety Guidance",
    description: "Expert advice on food and water safety, insect protection, and staying well in every region of the world.",
  },
]

const destinations = [
  "Africa", "Asia", "South America", "Central America", "Caribbean", "Middle East", "Oceania", "Europe",
]

const faqs = [
  {
    q: "When should I book my travel consultation?",
    a: "We recommend booking at least 4 to 6 weeks before departure. Some vaccines require multiple doses or time to become fully effective, so earlier is always better. Last-minute travellers are still welcome and we will do our best to optimize your protection.",
  },
  {
    q: "What should I bring to my appointment?",
    a: "Please have your travel itinerary, planned activities, immunization records (if available), and a list of current medications ready. The more details you share, the more tailored your care plan will be.",
  },
  {
    q: "Are consultations virtual or in person?",
    a: "Consultations are conducted virtually for your convenience. Any required vaccinations or prescriptions are coordinated with you based on your location.",
  },
  {
    q: "Do you provide documentation for travel?",
    a: "Yes. Where applicable, we provide vaccination records and certificates (such as Yellow Fever documentation) required for entry into certain countries.",
  },
]

function TravelHealth() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  })
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150])

  const [openFaq, setOpenFaq] = useState<number | null>(0)

  return (
    <div className="min-h-screen bg-[#FDFCFA]">
      <HavenNav />

      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[80vh] lg:min-h-[92vh] flex items-center overflow-hidden pt-[76px]">
        <motion.div
          style={{ y: heroY }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFCFA]/95 via-[#FDFCFA]/75 to-transparent z-10" />
          <img
            src="https://static.lumi.new/b9/b927cbaabcd48c4b8390cf3305419253.png"
            alt="Serene travel wellness"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="relative z-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-16 w-full pt-10 sm:pt-16 lg:pt-24">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E4E8E6]/80 backdrop-blur-sm mb-8"
            >
              <Plane className="w-4 h-4 text-[#7A8B82]" />
              <span className="text-[11px] sm:text-[12px] uppercase tracking-[0.25em] text-[#5E6B63]">Haven Travel Health</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-[clamp(2rem,7vw,5rem)] leading-[1.05] font-light text-[#2C2925] mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Travel the world, protected and prepared
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-[16px] sm:text-[18px] text-[#6B6761] mb-10 leading-relaxed max-w-lg font-light"
            >
              Expert travel medicine consultations, vaccinations, and preventative care so you can explore with confidence and peace of mind.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href={JANE_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-[#2C2925] text-white rounded-full text-[14px] tracking-[0.02em] hover:bg-[#1a1815] transition-all inline-flex items-center justify-center gap-3"
              >
                Book Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#services" className="px-8 py-4 text-[#2C2925] rounded-full text-[14px] tracking-[0.02em] border border-[#2C2925]/20 hover:border-[#2C2925]/50 transition-all inline-flex items-center justify-center">
                Explore Services
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro Statement */}
      <section className="py-16 sm:py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-16">
          <motion.div
            {...fadeUp}
            transition={{ duration: 1 }}
            className="max-w-4xl"
          >
            <p className="text-[13px] uppercase tracking-[0.3em] text-[#9B8E7E] mb-10">Safe. Prepared. Global.</p>
            <h2 className="text-[clamp(1.8rem,3.5vw,3.2rem)] leading-[1.3] text-[#2C2925] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Whether you are exploring a new continent, volunteering abroad, or visiting family overseas, Haven Travel Health ensures you are protected before you depart. As a member of the Institute of Travel Medicine, Dr. Saba Baig provides evidence-based, destination-specific care tailored to your journey.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 sm:py-20 lg:py-32 bg-[#F1F4F2]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-16">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8 }}
            className="mb-14 sm:mb-16"
          >
            <p className="text-[13px] uppercase tracking-[0.3em] text-[#7A8B82] mb-4">What We Offer</p>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-light text-[#2C2925]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Comprehensive travel care
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-[24px] p-8 lg:p-10 shadow-[0_20px_50px_-30px_rgba(44,41,37,0.2)] hover:-translate-y-1 transition-transform"
              >
                <div className="w-14 h-14 bg-[#E4E8E6] rounded-2xl flex items-center justify-center mb-7">
                  <service.icon className="w-6 h-6 text-[#7A8B82]" />
                </div>
                <h3 className="text-[24px] font-light text-[#2C2925] mb-4 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {service.title}
                </h3>
                <p className="text-[15px] text-[#6B6761] leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-16 sm:py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 mb-6">
                <Globe2 className="w-5 h-5 text-[#7A8B82]" />
                <p className="text-[13px] uppercase tracking-[0.3em] text-[#7A8B82]">Wherever You Go</p>
              </div>
              <h2 className="text-[clamp(2rem,3.5vw,3rem)] font-light text-[#2C2925] mb-8 leading-[1.2]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Care tailored to your destination
              </h2>
              <p className="text-[16px] text-[#6B6761] leading-relaxed mb-8">
                Every region carries unique health considerations. We provide destination-specific guidance and protection for travellers heading anywhere in the world.
              </p>
              <div className="flex flex-wrap gap-3">
                {destinations.map((d) => (
                  <span key={d} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F1F4F2] text-[13px] text-[#5E6B63]">
                    <MapPin className="w-3.5 h-3.5 text-[#7A8B82]" />
                    {d}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
              className="rounded-[28px] overflow-hidden"
            >
              <img
                src="https://static.lumi.new/49/49f490d4d93a31618ed5b11ef55f0e97.png"
                alt="Travel consultation"
                className="w-full h-[300px] sm:h-[400px] lg:h-[520px] object-cover object-center"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#F1F4F2]">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-16">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8 }}
            className="mb-12 sm:mb-16 text-center"
          >
            <p className="text-[13px] uppercase tracking-[0.3em] text-[#7A8B82] mb-4">Good to Know</p>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-light text-[#2C2925]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Frequently asked questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5 sm:py-6 text-left"
                >
                  <span className="text-[16px] sm:text-[18px] text-[#2C2925] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[#7A8B82] flex-shrink-0 transition-transform ${openFaq === index ? "rotate-180" : ""}`} />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="px-6 sm:px-8 pb-6"
                  >
                    <p className="text-[15px] text-[#6B6761] leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 lg:py-40 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#E4E8E6] rounded-full blur-[120px] opacity-50" />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-16 relative">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-[clamp(2.2rem,5vw,4rem)] font-light text-[#2C2925] mb-8 leading-[1.1]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ready for your next journey?
            </h2>
            <p className="text-[17px] text-[#6B6761] mb-12 font-light leading-relaxed">
              Book your travel health consultation and depart with confidence, knowing you are fully protected.
            </p>
            <a
              href={JANE_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group px-10 py-5 bg-[#2C2925] text-white rounded-full text-[14px] tracking-[0.02em] hover:bg-[#1a1815] transition-all inline-flex items-center justify-center gap-3"
            >
              Book Now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2925] text-white py-14 sm:py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-16 mb-14 sm:mb-16">
            <div>
              <h4 className="text-[28px] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven</h4>
              <p className="text-[14px] text-white/60 leading-relaxed">
                Clinical expertise.<br />Elevated wellness.
              </p>
            </div>
            <div>
              <h5 className="text-[12px] uppercase tracking-[0.2em] text-white/40 mb-5">Services</h5>
              <ul className="space-y-3 text-[14px] text-white/70">
                <li><Link to="/travelhealth" className="hover:text-white transition-colors">Travel Health</Link></li>
                <li><Link to="/acnehealth" className="hover:text-white transition-colors">Acne Health</Link></li>
                <li><Link to="/menopausehealth" className="hover:text-white transition-colors">Menopause Health</Link></li>
                <li><Link to="/havenwellnesshealth" className="hover:text-white transition-colors">Wellness & IV Therapy</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[12px] uppercase tracking-[0.2em] text-white/40 mb-5">Company</h5>
              <ul className="space-y-3 text-[14px] text-white/70">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
                <li><a href={JANE_APP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Book Consultation</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[12px] uppercase tracking-[0.2em] text-white/40 mb-5">Legal</h5>
              <ul className="space-y-3 text-[14px] text-white/70">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[13px] text-white/40">
              &copy; 2024 Haven Integrative Health Consulting Inc. All rights reserved.
            </p>
            <p className="text-[13px] text-white/40">
              Vancouver, British Columbia
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default TravelHealth
